/**
 * views/schema.ts —— `views/*.yaml` 声明形状（机读派生；U-930 v1.0 §5 映射）。
 * 闭集（越集即红）：
 *  · 槽 kind 九类＝U-930 §2（唯一源＝C 件 §0ter）
 *  · 字段可见性三值＝U-930 §3 可见性列（是/脱敏/内部 ⇒ visible/masked/internal）
 *  · 交互态六态＝U-930 §4（缺一即漂移）
 *  · 决策档三档＝门三档 allow/deny/escalate（内核 kernel/identity/gate.py 同形）
 */

export type SlotKind =
  | "文本／富文本" | "键值／字段组" | "列表／表格" | "状态／徽标" | "时间线／流"
  | "卡（动作）" | "图／图表" | "表单／输入" | "容器／布局";

export const SLOT_KINDS: readonly SlotKind[] = [
  "文本／富文本", "键值／字段组", "列表／表格", "状态／徽标", "时间线／流",
  "卡（动作）", "图／图表", "表单／输入", "容器／布局",
];

export type Visibility = "visible" | "masked" | "internal";

export const VISIBILITIES: readonly Visibility[] = ["visible", "masked", "internal"];

export type ViewState =
  | "empty" | "loading" | "error" | "forbidden" | "overLimit" | "degraded";

/** U-930 §4 六态（缺一即漂移） */
export const VIEW_STATES: readonly ViewState[] = [
  "empty", "loading", "error", "forbidden", "overLimit", "degraded",
];

/** 门三档（内核 kernel/identity/gate.decide 同形闭集） */
export type Verdict = "allow" | "deny" | "escalate";

export const VERDICTS: readonly Verdict[] = ["allow", "deny", "escalate"];

/** 字段声明（U-930 §3 字段摆放矩阵行） */
export interface FieldDecl {
  key: string;
  placement: string;
  visibility: Visibility;
  readonly: boolean;
  source?: string;
  note?: string;
  /** 值空时的呈现文案（§3 decided_by 行「空 ⇒ 显示"未决"」的机读形态） */
  empty_display?: string;
}

export interface SlotDecl {
  id: string;
  kind: SlotKind;
  component: string;
  fields: string[];
  /** 段位标签（取 U-930 §3 位置列原文；缺省不出标签，不内嵌版式） */
  title?: string;
}

export interface PanelDecl {
  id: string;
  title: string;
  component: string;
  slots: string[];
}

/** 决策声明（§3 行4「action（同意/拒绝/升级）」；经 gate.call；必落账） */
export interface DecisionDecl {
  slot: string;
  options: string[];
  verdicts: Record<string, Verdict>;
  must_ledger: boolean;
}

/** 视图声明（views/*.yaml 顶层） */
export interface ViewDecl {
  name: string;
  title: string;
  layout_version: string;
  panels: PanelDecl[];
  slots: SlotDecl[];
  fields: FieldDecl[];
  states: ViewState[];
  decisions?: DecisionDecl;
}
